/* =========================================================================
   platform-types.ts — FROZEN platform coordination contracts.
   Imported by: compare screen, versioning, multi-site, census workstreams.
   Pure types only — NO runtime, NO React, NO model import cycles.
   ========================================================================= */

import type { Scenario, ModelResult, CensusArea, AuditEntry } from "./types";
import type { FxSnapshot } from "./reporting-currency";

/* ───────────────────────── MULTI-SITE ───────────────────────── */

/** A named grouping of scenarios. Phase-able; nullable seam. */
export interface Site {
  id: string; // 'site-<ts>' app-generated (matches scenario id style)
  name: string;
  description?: string;
  createdAt?: number; // epoch ms (client clock; mirrors auditLog timestamps)
  updatedAt?: number;
}

/* ───────────────────────── VERSIONING ───────────────────────── */

/** F4 — a compact set of headline KPIs captured at version-save time, used ONLY to detect that
 *  model.ts changed between save and restore (NOT for display). Keep it small + stable: the few
 *  numbers a board actually approves. Extend by agreement (frozen, additive-only). */
export interface ComputeFingerprint {
  developerIrr: number | null;
  developerNpv: number | null;
  yieldOnCost: number | null;
  totalCost: number | null;
  year1Ebitda: number | null;
  // additive-only; keep ≤ ~8 numbers. Compared with a tolerance (see computeFingerprint helper).
}

/** An append-only, immutable point-in-time snapshot of ONE scenario.
 *  `snapshot` is the FULL Scenario blob (inputs only) — results re-derive via
 *  Model.compute(snapshot). This is the governance unit ("the v3 board approved"). */
export interface ScenarioVersion {
  id: string; // uuid (server gen_random_uuid) or client uuid pre-sync
  scenarioId: string; // -> Scenario.id
  label: string; // human label, e.g. "v3 — board-approved 2026-06-16"
  note?: string; // optional governance note
  snapshot: Scenario; // FROZEN deep clone of the Scenario at capture time (INPUTS only)
  schemaVersion: number; // === snapshot.schemaVersion at capture (default 1)
  /** F4 (drift-detection): the KPIs Model.compute(snapshot) produced AT SAVE TIME. Stored, never
   *  displayed; on restore/diff, if Model.compute(snapshot) TODAY ≠ this, model.ts changed since
   *  approval → surface a "model changed since approved" banner. Closes the "store-inputs only does
   *  NOT close drift across compute() changes" governance hole. Captured by VERSIONING via
   *  computeFingerprint(Model.compute(snapshot)) (FOUNDATION provides the fingerprint helper §3.2). */
  computeFingerprint?: ComputeFingerprint;
  createdAt: number; // epoch ms — SERVER-STAMPED on insert (client value is informational only)
  createdBy?: string; // auth user id (undefined in passcode mode) — SERVER-DEFAULT auth.uid()
  /** F1 (attribution that survives passcode mode): free-text "Saved by" captured on the
   *  Save-Version dialog. createdBy is always undefined in the shipped passcode deployment, so this
   *  is the ONLY real attribution. Surface "unattributed" in UI if absent. */
  createdByLabel?: string;
}

/** What the Versions screen lists (metadata without the heavy snapshot blob,
 *  for cheap listing). Hydrate the full ScenarioVersion only on restore/diff. */
export interface ScenarioVersionMeta {
  id: string;
  scenarioId: string;
  label: string;
  note?: string;
  schemaVersion: number;
  createdAt: number;
  createdBy?: string;
  createdByLabel?: string;
}

/* ─────────────────────────── COMPARE ─────────────────────────── */

/** A single comparable metric across N scenarios, with deltas vs a baseline column.
 *  Compare is a PURE derivation over `scenarios` + the existing `results` map
 *  (state.tsx:450) — NO schema, NO persistence, NO model change. */
export type CompareCategory =
  | "land"
  | "costStack"
  | "buildTiming"
  | "rent"
  | "developerReturns"
  | "operatorAffordability"
  | "market"
  | "programme"
  | "funding"
  | "ownership";

export interface CompareCell {
  scenarioId: string;
  /** Raw numeric value (or null if N/A for this scenario). */
  value: number | null;
  /** Pre-formatted display string (currency/pct/year), using existing Fmt.* helpers. */
  display: string;
  /** Signed delta vs the baseline column's value (null for the baseline itself / N/A, and null whenever
   *  this cell or the baseline cell is a caveated non-converged fallback — a delta is never built from a
   *  fabricated operand). */
  deltaVsBaseline: number | null;
  /** Pre-formatted delta string, e.g. '+$1.2M' / '−0.8pp'. */
  deltaDisplay: string | null;
  /** When a value is a non-converged fallback (e.g. breakEvenRentConverged===false),
   *  set true so the UI can caveat the cell. */
  caveated?: boolean;
  /** Set when a delta WOULD have been shown but was suppressed because an operand is a caveated
   *  fallback — so the UI can disclose why the swing is missing rather than leave a silent blank. */
  deltaSuppressed?:
    "self" | "baseline" | "basis" | "method" | "horizon" | "window";
  /** For rows whose value depends on the developer's exit strategy: the basis this cell was computed on
   *  ("sale at PC" or "Y5 exit"). Deltas are only built between cells on the same basis. */
  basis?: string;
}

export interface CompareRow {
  /** Stable key, e.g. 'kpis.developerIrr' or 'assumptions.purchasePrice'. */
  key: string;
  label: string; // human row label (reuse DRIVERS[] labels where possible)
  category: CompareCategory;
  unit:
    "currency" | "pct" | "pp" | "year" | "months" | "count" | "ratio" | "date";
  cells: CompareCell[]; // one per selected scenario, in column order
  /** Optional "what drives the gap" attribution note for this row. */
  driver?: string;
  /** True when the selected scenarios compute this row on different exit-strategy bases, so the cells
   *  are not like-for-like and deltas against the baseline are suppressed. */
  basisMixed?: boolean;
}

export interface CompareViewModel {
  scenarioIds: string[]; // 2..4 selected, in column order
  baselineId: string; // which column deltas are measured against
  rows: CompareRow[];
  /** True if ANY market/census row is still reading the static singleton
   *  (i.e. the CENSUS per-scenario workstream has not landed for these scenarios).
   *  Compare MUST label market cells "static North Ryde" while this is true. */
  marketIsStatic: boolean;
  metadata?: {
    valuationDate?: string;
    endDate?: string;
    commonHorizon?: string;
    fx?: FxSnapshot;
    /** Inputs that differ from the baseline. This is an input inventory, not
     * a causal explanation of any return movement. */
    changedInputs?: Record<string, string[]>;
    /** Additive dated cost-bucket deltas; a bucket appears once only. */
    costBridge?: Record<string, number | null>;
  };
}

/** The pure builder the COMPARE workstream implements + unit-tests with
 *  allScenarios/allResults fixtures (MODEL-TESTING §3.5). Signature frozen here so
 *  the screen and the test target the same contract.
 *
 *  C-5 (review) CLARIFICATION — COLUMN KEYS ARE OPAQUE, NOT strictly `Scenario.id`:
 *  `scenarioIds` are COLUMN keys; they need NOT be live `Scenario.id`s. The VERSIONING diff
 *  reuses this builder to compare TWO snapshots of the SAME scenario by passing SYNTHETIC keys
 *  (e.g. `${scenarioId}@${versionId}`) with a matching `scenarios`/`results` map keyed by those
 *  same synthetic keys. Without this, two versions of one scenario collide on one `id` and the
 *  compare collapses to a single column. Implementations MUST key `results`/`scenarios` lookups by
 *  the provided `scenarioIds` entries, NOT re-derive from `scenario.id`. */
export type BuildCompareViewModel = (args: {
  scenarios: Scenario[]; // keyed-by-column: scenarios[i].___ may be a synthetic-keyed clone
  results: Record<string, ModelResult>; // keyed by the column keys below (NOT necessarily Scenario.id)
  scenarioIds: string[]; // COLUMN keys (opaque); 2..4 in display order
  baselineId: string; // one of scenarioIds
  fx?: FxSnapshot;
  valuationDate?: string;
  endDate?: string;
}) => CompareViewModel;

/* ─────────────────── PER-SCENARIO MARKET / CENSUS ─────────────── */

/** C-8 (review) RESOLUTION: `ScenarioCatchment` alias is DELETED — it was a half-used alias for
 *  `CensusArea` (an exact drift seed). Everyone uses `CensusArea` directly (census.ts:8). COMPARE
 *  and CENSUS specs that imported `ScenarioCatchment` are updated to import `CensusArea`. */

/** The resolver contract the CENSUS workstream implements in census.ts.
 *  Frozen here so compare/exports call the SAME accessor with the SAME precedence (below). */
export type CatchmentResolver = (scenario: Scenario) => CensusArea;

/** C-4 / F3 (review) RATIFIED: the per-scenario market seam is TWO fields, BOTH declared by
 *  FOUNDATION on the single shared types.ts commit (§3.1):
 *   1. `assumptions.catchmentCode?: string`  — the PICKER's write target. Written via the EXISTING
 *      `updateAssumption('catchmentCode', code)` action ⇒ ZERO new state.tsx action, ZERO
 *      ActionsContext collision, and FREE auditLog entries (governance bonus). It IS a per-scenario
 *      lever ⇒ added to `SCENARIO_LEVER_KEYS` (model.ts) so `pushSharedToAll` preserves each
 *      scenario's market (N3 review — otherwise pushing shared structure silently resets markets).
 *   2. `Scenario.catchment?: CensusArea`     — the SELF-CONTAINED embedded snapshot, for
 *      share/restore/version (a saved blob carries its market even without the registry).
 *  FROZEN RESOLVER PRECEDENCE (do not reorder): code → blob → DEFAULT_CATCHMENT.
 *  i.e. `marketByCode(scenario.assumptions.catchmentCode) ?? scenario.catchment ?? DEFAULT_CATCHMENT`.
 *  This is the contract COMPARE's market rows + every export read. compute() does NOT read either
 *  field (display-only, CENSUS §2.4 Option A default) — the golden numbers stay frozen. */

/* ─────────────────────────── AUDIT ──────────────────────────── */

/** Re-export so the versioning/diff UI imports the audit type from ONE place. */
export type { AuditEntry };

/** Cap applied to Scenario.auditLog (currently UNBOUNDED — PERSISTENCE §4). */
export const AUDIT_LOG_CAP = 500;

/** Pagination cap for the Versions list (F10 review — unbounded version growth). */
export const VERSION_PAGE_SIZE = 25;

/** F4 fingerprint tolerance — KPIs compared with this relative tolerance to allow float jitter
 *  while catching real model.ts changes. */
export const FINGERPRINT_REL_TOLERANCE = 1e-6;

/* ───────────────────────── RUNTIME helpers ───────────────────────── */

/** F4: project a ModelResult down to the stored fingerprint. VERSIONING calls this at save time
 *  with Model.compute(snapshot), stores the result, and re-calls it at restore/diff to compare. */
export function computeFingerprint(r: ModelResult): ComputeFingerprint {
  return {
    developerIrr: r.developer.irr ?? null,
    developerNpv: r.kpis.developerNpv ?? null,
    yieldOnCost: r.developer.yieldOnCost ?? null,
    totalCost: r.developer.totalCost ?? null,
    year1Ebitda: r.kpis.yearOneEbitda ?? null,
  };
}

/** F4: true iff every KPI matches within FINGERPRINT_REL_TOLERANCE (null===null ok). A false
 *  result means model.ts changed since the version was approved → UI shows "model changed" banner. */
export function fingerprintMatches(
  a: ComputeFingerprint,
  b: ComputeFingerprint,
): boolean {
  const keys: (keyof ComputeFingerprint)[] = [
    "developerIrr",
    "developerNpv",
    "yieldOnCost",
    "totalCost",
    "year1Ebitda",
  ];
  for (const k of keys) {
    const av = a[k];
    const bv = b[k];
    if (av === null && bv === null) continue;
    if (av === null || bv === null) return false;
    if (av === 0 && bv === 0) continue;
    const denom = Math.max(Math.abs(av), Math.abs(bv));
    if (denom === 0) continue;
    if (Math.abs(av - bv) / denom > FINGERPRINT_REL_TOLERANCE) return false;
  }
  return true;
}
